/* eslint-disable @typescript-eslint/no-explicit-any */

import { Machine, assign } from 'xstate';
import {
  IDocs,
  IFinalResponse,
  IGenericResponseData,
  MetadataTypes,
} from '_core/types';
import { getReturnResponseDefault, prepareGenericData } from '_core/util';
import { GenericContext, GenericEvents, GenericStateSchema } from '../Types';

export const generateGenericDocs = (
  docsParams: {
    title: string;
    description: string;
    version: string;
    codeToExecute: string;
  },
  definitions: {
    API_DOMAIN: string;
    ENDPOINT_API_PATH: string;
    URL_POSTMAN: string;
  },
): IDocs => {
  return {
    ...docsParams,
    apis: [
      {
        name: definitions.API_DOMAIN,
        path: definitions.ENDPOINT_API_PATH,
        linkPostman: definitions.URL_POSTMAN,
        order: 1,
      },
    ],
  };
};

type DocsParamType = {
  docs: IDocs;
  definitions: any;
};

const handlePrepareData = (
  ctx: GenericContext,
  docsParam: DocsParamType,
  handleResponse?: any,
): IFinalResponse => {
  const keys = Object.keys(ctx);

  const metadata = {
    machineName: docsParam.docs.title,
    machineVersion: docsParam.docs.version,
  };

  const listOfDefs = [docsParam.definitions];

  const genericData = prepareGenericData(
    [ctx.invokeApiResponse],
    keys,
    listOfDefs,
  );

  const finalResponse = handleFinalResponse(
    genericData,
    metadata,
    ctx.inputParameters,
    handleResponse,
  );

  return finalResponse;
};

const handleFinalResponse = (
  genericData: IGenericResponseData,
  metadata: MetadataTypes,
  inputParameters: any,
  handleResponse?: any,
) => {
  const { errors, data, infos } = genericData;

  let finalReturn = getReturnResponseDefault(metadata);

  const hasNoErrors = errors?.length === 0;
  if (hasNoErrors) {
    let dataResponse = data;
    if (handleResponse && handleResponse.handleSucessFinalResponseFunction) {
      const handleSucessFinalResponseFunction =
        handleResponse.handleSucessFinalResponseFunction;
      if (typeof handleSucessFinalResponseFunction === 'function')
        dataResponse = handleSucessFinalResponseFunction(data, inputParameters);
    }

    finalReturn = {
      ...finalReturn,
      success: true,
      data: dataResponse,
    };
  } else {
    let flowErrors = { smilesErrorService: true };
    if (handleResponse && handleResponse.handleErrorFlowFinalResponseFunction) {
      const handleErrorFlowFinalResponseFunction =
        handleResponse.handleErrorFlowFinalResponseFunction;
      if (typeof handleErrorFlowFinalResponseFunction === 'function')
        flowErrors = handleErrorFlowFinalResponseFunction(infos);
    }

    finalReturn = {
      ...finalReturn,
      success: false,
      errors: {
        flow: flowErrors,
        infos,
        details: errors,
      },
    };
  }

  return finalReturn;
};

// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
export const GenericMachine = (
  invokeApiFunction: any,
  paramDocs: DocsParamType,
  handleResponse?: {
    handleSucessFinalResponseFunction?: any;
    handleErrorFlowFinalResponseFunction?: any;
  },
) => {
  return Machine<GenericContext, GenericStateSchema, GenericEvents>({
    id: `${paramDocs.docs.title}-machine`,
    initial: 'IDLE',
    context: {
      inputParameters: null,
      invokeApiResponse: null,
      finalResponse: null,
    },
    states: {
      IDLE: {
        on: {
          START: 'INVOKE_API',
        },
      },
      INVOKE_API: {
        invoke: {
          id: `${paramDocs.docs.title}-invoke`,
          src: (ctx, state) => {
            ctx.inputParameters = state;
            if (invokeApiFunction && typeof invokeApiFunction === 'function') {
              return invokeApiFunction(state, ctx);
            }
          },
          onDone: {
            target: 'PREPARE_DATA',
            actions: assign({
              invokeApiResponse: (_, state) => state.data,
            }),
          },
          onError: {
            target: 'PREPARE_DATA',
            actions: assign({
              invokeApiResponse: (_, state) => state.data,
            }),
          },
        },
      },
      PREPARE_DATA: {
        id: 'prepareData',
        always: [
          {
            id: 'preparing',
            target: 'FINISH',
            actions: assign({
              finalResponse: (ctx) =>
                handlePrepareData(ctx, paramDocs, handleResponse),
            }),
          },
        ],
      },
      FINISH: {
        type: 'final',
      },
    },
  });
};
